import { useEffect, useId, useRef, useState } from 'react'
import { Globe, Loader2, Save, X } from 'lucide-react'
import { fetchSites, saveDefMeta } from './defAdmin'
import { useScrimDismiss } from '../shared/hooks/useScrimDismiss'
import { useEscapeClose } from '../shared/hooks/useEscapeClose'

/**
 * Pop-in « Informations du bloc » : ce qu'un bloc RACONTE de lui-même dans le
 * catalogue — son nom, sa description, sa catégorie — et à qui il appartient.
 *
 * Pourquoi une pop-in alors que l'édition du builder se fait en place partout :
 * ces quatre valeurs ne sont pas rendues sur le canevas. Un nom de catalogue,
 * une catégorie de rangement et un rattachement à un site n'ont aucun élément à
 * l'écran où se cliquer — l'édition en place n'a rien à quoi s'accrocher. Le
 * bloc lui-même (son contenu, son rendu) reste, lui, édité en place.
 *
 * Ce que la pop-in NE fait pas :
 *  - renommer le type machine (`block.type`) : il lie chaque instance à sa
 *    définition, le changer les orphelinerait toutes. Il est montré, pas édité ;
 *  - toucher au corps de la définition (`rjBuild`) : c'est l'affaire de
 *    l'assistant, et de lui seul.
 *
 * Elle se monte dans le scrim du catalogue (`modal-scrim-over`), donc son propre
 * scrim stoppe la propagation du clic — sinon fermer cette pop-in fermerait
 * aussi le catalogue derrière. Échap est géré par le catalogue, un niveau à la
 * fois.
 *
 * @param {object} block — l'entrée de registry éditée.
 * @param {string[]} categories — les catégories déjà en usage, proposées en
 *   autocomplétion : le champ reste libre (une nouvelle catégorie doit pouvoir
 *   naître) mais on ne crée pas « Editorial » à côté d'« Éditorial » par étourderie.
 * @param {(patch: object) => void} onSaved — reçoit la réponse de Drupal
 *   (valeurs RELUES du document, plus le YAML stocké).
 */
export const BuilderDefMetaModal = ({ block, categories = [], onClose, onSaved }) => {
  const scrim = useScrimDismiss(onClose)
  useEscapeClose(onClose)
  const titleId = useId()
  const nameId = useId()
  const categoryId = useId()
  const descriptionId = useId()
  const siteId = useId()
  const listId = useId()
  const nameRef = useRef(null)

  const [name, setName] = useState(block.name ?? '')
  const [category, setCategory] = useState(block.category ?? '')
  const [description, setDescription] = useState(block.description ?? '')
  const [site, setSite] = useState(block.siteId == null ? '' : String(block.siteId))
  const [sites, setSites] = useState([])
  const [sitesError, setSitesError] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    const previous = document.activeElement
    nameRef.current?.focus()
    nameRef.current?.select()
    return () => { if (previous instanceof HTMLElement) previous.focus() }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    fetchSites(controller.signal)
      .then(setSites)
      // Le seul échec qu'on avale, c'est notre propre annulation : le composant
      // est démonté, il n'y a plus d'écran à renseigner. Un `AbortError` venu
      // d'ailleurs est un échec comme un autre — le taire laisserait une liste
      // vide sans un mot, alors que le bloc a peut-être un site à conserver.
      .catch(() => { if (!controller.signal.aborted) setSitesError(true) })
    return () => controller.abort()
  }, [])

  const handleSubmit = async (event) => {
    event.preventDefault()
    const trimmed = name.trim()
    if (!trimmed || saving) return

    setSaving(true)
    setError(null)
    try {
      const saved = await saveDefMeta({
        nid: block.defNid,
        name: trimmed,
        category: category.trim(),
        description: description.trim(),
        siteId: site === '' ? null : Number(site),
      })
      onSaved(saved)
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  return (
    <div
      className="modal-scrim modal-scrim-over"
      {...scrim}
    >
      <form
        className="modal-panel max-w-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={e => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="modal-header">
          <div className="flex min-w-0 items-center gap-3">
            <h2 id={titleId} className="truncate">Informations du bloc</h2>
            <span className="truncate font-mono text-xs font-normal text-slate-400">{block.type}</span>
          </div>
          <button type="button" onClick={onClose} className="btn btn-ghost" aria-label="Fermer">
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="modal-body flex-1 space-y-4">
          <div>
            <label className="field-label mb-1.5" htmlFor={nameId}>Nom</label>
            <input
              id={nameId}
              ref={nameRef}
              type="text"
              className="input"
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={255}
              required
              aria-describedby={`${nameId}-help`}
            />
            <p id={`${nameId}-help`} className="mt-1.5 text-xs text-slate-400">
              Le nom affiché dans le catalogue. L'identifiant technique du bloc
              ({block.type}) ne change pas : c'est lui qui relie les blocs déjà
              posés à cette définition.
            </p>
          </div>

          <div>
            <label className="field-label mb-1.5" htmlFor={categoryId}>Catégorie</label>
            <input
              id={categoryId}
              type="text"
              className="input"
              value={category}
              onChange={e => setCategory(e.target.value)}
              list={listId}
              maxLength={64}
              placeholder="Structure, Éditorial…"
            />
            <datalist id={listId}>
              {categories.map(c => <option key={c} value={c} />)}
            </datalist>
            <p className="mt-1.5 text-xs text-slate-400">
              Le groupe sous lequel le bloc est rangé dans le catalogue. Vide, il
              rejoint « Général ».
            </p>
          </div>

          <div>
            <label className="field-label mb-1.5" htmlFor={descriptionId}>Description</label>
            <textarea
              id={descriptionId}
              className="textarea"
              rows={4}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="À quoi sert ce bloc, et quand le choisir."
            />
          </div>

          <div>
            <label className="field-label mb-1.5" htmlFor={siteId}>Site</label>
            <select
              id={siteId}
              className="select"
              value={site}
              onChange={e => setSite(e.target.value)}
              disabled={sitesError}
            >
              <option value="">Bibliothèque partagée (aucun site)</option>
              {sites.map(s => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
              {/* Le site actuel n'est pas dans la liste (droits, site supprimé) :
                  on l'y ajoute plutôt que de faire retomber le champ sur
                  « aucun site » — l'enregistrement détacherait le bloc sans que
                  personne ne l'ait demandé. */}
              {block.siteId != null && !sites.some(s => s.id === block.siteId) && (
                <option value={String(block.siteId)}>{block.siteName || `Site ${block.siteId}`}</option>
              )}
            </select>
            <p className="mt-1.5 flex items-start gap-1.5 text-xs text-slate-400">
              <Globe size={13} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                Un bloc rattaché à un site n'est proposé que sur les pages de ce
                site. Sans site, il rejoint la bibliothèque partagée, proposée
                partout ailleurs.
                {sitesError && ' La liste des sites n\'a pas pu être chargée : le rattachement actuel est conservé.'}
              </span>
            </p>
          </div>

          {error && (
            <p role="alert" className="alert alert-danger">{error}</p>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" onClick={onClose} className="btn btn-secondary">Annuler</button>
          <button type="submit" className="btn btn-primary" disabled={!name.trim() || saving}>
            {saving
              ? <><Loader2 size={16} className="animate-spin" aria-hidden="true" />Enregistrement…</>
              : <><Save size={16} aria-hidden="true" />Enregistrer</>}
          </button>
        </div>
      </form>
    </div>
  )
}
